import { createElement, useMemo } from 'react';
import { instanceDe, useDeclarationsPort } from '@taki/ui';
import type { DeclarationsPort, FicheInstanciee } from '@taki/ui';
import { IconeDeFiche } from './appRegistry.js';
import { actionsDuDock } from './raccourcis.js';
import type { AppActionDecl, ShellAppEntry } from './types.js';

/**
 * LES MOTS DE L'ORG PAR-DESSUS CEUX DU BUNDLE, EN UN SEUL ENDROIT.
 *
 * LE PROBLEME QU'IL FERME. Une org qui renomme une app (chantier
 * `app-studio-client`) ecrit ce nom dans SA declaration, pas dans le bundle. Le
 * shell, lui, lit le libelle et l'icone d'une app a une douzaine d'endroits :
 * le dock, le menu home, le rail du hub Parametres, le titre de l'onglet, la
 * liste des apps, l'ecran des utilisateurs. Appliquer l'instance dans chacun
 * d'eux serait douze occasions d'en oublier un, et le premier oubli afficherait
 * deux noms differents pour la meme app dans la meme fenetre.
 *
 * LA PROJECTION SE FAIT DONC LA OU LE SHELL LIT SON REGISTRE, une fois :
 * `useEntreesEffectives` remplace le libelle et l'icone des entrees, et tous
 * les lecteurs continuent de lire `entry.label` / `entry.icon` sans savoir
 * qu'une instance existe.
 *
 * CE QUI NE BOUGE JAMAIS, et pour la meme raison que dans `resolution.ts` de
 * `@taki/app-runtime` : l'`id` (clef de `installedApps` et des preferences du
 * poste), la route (une adresse partageable) et les `entities`, dont les rules
 * derivent les portes d'acces. Un client renomme son app, il ne deplace pas
 * ses donnees.
 */

/** Ce qu'une instance peut changer aux MOTS d'une entree de registre. */
export interface MotsDeLOrg {
  /** Le libelle affiche partout : `menu.label` de l'instance, puis son `name`. */
  label?: string;
  /** Le nom d'icone choisi par l'org, resolu par la map curatee du shell. */
  icon?: string;
  /**
   * LES ECRANS DE L'ORG, quand elle en a : ses portes (renommees, ajoutees,
   * retirees) donnent au dock SES raccourcis, jamais ceux du modele.
   */
  ecrans?: unknown;
}

/**
 * LES MOTS QU'UNE FICHE INSTANCIEE APPORTE, ou rien.
 *
 * `menu.label` PASSE AVANT `name`, et ce n'est pas arbitraire : c'est le
 * libelle du menu que le shell affiche (`resolveAppEntry` derive `label` de
 * `fiche.menu.label`). Une org qui n'a renomme que le NOM de l'app voit quand
 * meme ce nom dans le dock, parce qu'une app renommee dont le menu garde
 * l'ancien mot ne s'explique pas.
 */
export function motsDeLInstance(fiche: FicheInstanciee | undefined): MotsDeLOrg {
  if (fiche === undefined) return {};
  const label = fiche.menu?.label ?? fiche.name;
  return {
    ...(label === undefined || label.trim() === '' ? {} : { label }),
    ...(fiche.icon === undefined || fiche.icon.trim() === '' ? {} : { icon: fiche.icon }),
  };
}

/**
 * UNE ENTREE DE REGISTRE VUE PAR CETTE ORG. Sans port, sans instance ou sans
 * mot change, c'est L'ENTREE ELLE-MEME qui est rendue, a l'identite pres : une
 * copie a chaque rendu remonterait les composants qui la recoivent en prop.
 */
export function ficheEffective(
  entry: ShellAppEntry,
  port: DeclarationsPort | null,
): ShellAppEntry {
  return avecLesMots(entry, ceQueLOrgChange(instanceDe(port, entry.id)));
}

/** Les mots de la fiche instanciee, plus les ecrans de l'org quand elle en a. */
function ceQueLOrgChange(
  instance: { fiche?: FicheInstanciee; ecrans?: unknown } | null | undefined,
): MotsDeLOrg {
  return {
    ...motsDeLInstance(instance?.fiche),
    ...(instance?.ecrans === undefined ? {} : { ecrans: instance.ecrans }),
  };
}

/** L'entree, ses mots remplaces. La meme reference quand il n'y a rien a remplacer. */
function avecLesMots(entry: ShellAppEntry, mots: MotsDeLOrg): ShellAppEntry {
  const actions = actionsDeLOrg(entry, mots.ecrans, mots.icon ?? entry.fiche?.icon);
  if (mots.label === undefined && mots.icon === undefined && actions === undefined) return entry;
  return {
    ...entry,
    ...(actions === undefined ? {} : { actions }),
    ...(mots.label === undefined ? {} : { label: mots.label }),
    ...(mots.icon === undefined
      ? {}
      : {
          /* LE NOM D'ICONE SE RESOUT PAR LA MAP CURATEE DU SHELL, la meme que
             les raccourcis du dock, et un nom qu'elle ne connait pas retombe
             sur l'icone NATIVE de l'app : un mot mal tape ne doit pas effacer
             l'icone que tout le monde reconnait. */
          icon: createElement(IconeDeFiche, { nom: mots.icon, repli: entry.fiche?.icon }),
        }),
  };
}

/**
 * LES RACCOURCIS DE L'APP LITE : ceux de SES portes, ou `undefined` quand rien
 * ne change (pas d'ecrans d'org, ou les memes raccourcis que le modele). Le
 * `undefined` garde l'identite de l'entree, donc aucun rendu en aval.
 */
function actionsDeLOrg(
  entry: ShellAppEntry,
  ecrans: unknown,
  icone: string | undefined,
): AppActionDecl[] | undefined {
  if (ecrans === undefined || entry.declaration === undefined) return undefined;
  const actions = actionsDuDock(entry.actions, ecrans, icone);
  const avant = entry.actions ?? [];
  const memes =
    actions.length === avant.length &&
    actions.every(
      (action, rang) =>
        action.id === avant[rang]?.id &&
        action.label === avant[rang]?.label &&
        action.iconName === avant[rang]?.iconName,
    );
  return memes ? undefined : actions;
}

/**
 * LE REGISTRE DU SHELL, VU PAR CETTE ORG.
 *
 * L'IDENTITE DE LA LISTE EST PRESERVEE quand rien n'est personnalise : sans
 * instance, la liste rendue est CELLE qu'on a recue, donc aucun `useMemo` en
 * aval ne se recalcule et aucun ecran ne se remonte.
 */
export function useEntreesEffectives(entrees: readonly ShellAppEntry[]): ShellAppEntry[] {
  /* `instances` EST LA DEPENDANCE, pas le port : l'hote reconstruit son objet
     de port des qu'un droit ou une erreur bouge, alors que la Map des instances
     ne change qu'a l'arrivee d'un snapshot. */
  const instances = useDeclarationsPort()?.instances;
  return useMemo(() => {
    const projetees = entrees.map((entree) =>
      avecLesMots(entree, ceQueLOrgChange(instances?.get(entree.id))),
    );
    return projetees.every((entree, rang) => entree === entrees[rang])
      ? (entrees as ShellAppEntry[])
      : projetees;
  }, [entrees, instances]);
}
